import { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { State } from "../../redux";
import { Modal, Text, ColorV2, Button } from "@adoptaunabuelo/react-components";
import { X } from "lucide-react";
import media from "styled-media-query";

const Container = styled.div`
	display: flex;
	flex-direction: row;
	${media.lessThan("medium")`
        flex-direction: column;
	`}
`;
const ColLeft = styled.div`
	display: flex;
	flex-direction: column;
	background-color: ${ColorV2.surface.background};
	width: 250px;
	padding: 40px 32px 32px;
	gap: 16px;
	border-radius: 0px 24px 24px 0px;
	${media.lessThan("medium")`
        width: unset;
        border-radius: 0px 0px 24px 24px;
        padding: 40px 24px 32px;
	`}
`;
const ColRight = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: 40px 32px;
	gap: 32px;
	justify-content: center;
	${media.lessThan("medium")`
        padding: 40px 24px;
	`}
`;
const MainIcon = styled.div`
	height: 64px;
	width: 64px;
	padding: 4px;
`;
const Section = styled.div`
	display: flex;
	flex-direction: column;
	background-color: white;
`;
const PriceView = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
	padding: 12px 16px;
	gap: 16px;
	align-items: center;
	${media.lessThan("medium")`
		flex-direction: column;
		align-items: flex-start;
		gap: 0px;
	`}
`;
const PriceRow = styled.div`
	display: flex;
	flex-direction: row;
	width: 100%;
	border: 1px solid ${ColorV2.border.neutralSoft};
	border-radius: 16px;
	margin-top: 16px;
	overflow: hidden;
`;
const Icon = styled.img`
	height: 64px;
	width: 64px;
	margin-bottom: 16px;
`;
const Col = styled.div`
	display: flex;
	flex-direction: column;
`;

const ReliefModal = (props: Props) => {
	const isMobile = window.screen.width <= 768;
	const translation = useSelector(
		(state: State) => state.location.translation
	);
	const [priceAmount, setPriceAmount] = useState("0");

	useEffect(() => {
		getReliefAmount(props.price, props.charge);
	}, [props.price, props.charge]);

	const getReliefAmount = (price: number, charge: string) => {
		const totalAmount = charge === "monthly" ? price * 12 : price;
		const reliefSection1 = totalAmount > 250 ? 250 : totalAmount;
		const reliefSection2 = totalAmount > 250 ? totalAmount - 250 : 0;
		const relief = reliefSection1 * 0.8 + reliefSection2 * 0.4;
		setPriceAmount((relief / (charge === "monthly" ? 12 : 1)).toFixed(2));
	};

	return (
		<Modal
			isVisible={props.isVisible}
			style={{ width: 820 }}
			type={isMobile ? "full-screen" : "default"}
			contentStyle={{ padding: 0 }}
			titleStyle={{ position: "unset" }}
			hideClose={true}
			onClose={props.onClose}
		>
			<Container>
				<ColLeft>
					<MainIcon>
						<Icon
							src={require("../../assets/images/icon/coins.webp")}
						/>
					</MainIcon>
					<Text type="h3" weight="semibold">
						Recupera hasta el 80% de lo que dones
					</Text>
				</ColLeft>
				<ColRight>
					<Button
						style={{
							position: "absolute",
							top: 8,
							right: 8,
							backgroundColor: "white",
							borderRadius: 48,
						}}
						design={"image"}
						icon={<X color={ColorV2.text.neutralHard} />}
						onClick={props.onClose}
					/>
					<Section
						style={{
							backgroundColor: "transparent",
							paddingTop: isMobile ? 0 : 16,
						}}
					>
						<Text type="p" weight="medium">
							Puedes deducirte tu donación en la Declaración de la
							Renta. De los primeros 250 € que dones te
							desgravarás el 80% y del resto el 40%*.
						</Text>
						<Text type="p2" style={{ marginTop: 16, color: ColorV2.text.neutralMedium }}>
							Además, si colaboras con nosotros con una donación
							igual o superior durante al menos 3 años, la
							desgravación se incrementa hasta el 45%.
						</Text>
					</Section>
					<Section>
						<Text type="p2" weight="medium">
							Esto significa que:
						</Text>
						<PriceRow>
							<PriceView>
								<Icon
									src={require("../../assets/images/icon/donation.webp")}
								/>
								<Col>
									<Text type="p" weight="medium">
										Si donas
									</Text>
									<Text type={"h2"} weight="semibold">
										{props.price}
										<span
											style={{
												fontSize: 18,
												fontWeight: 500,
											}}
										>
											,00€
										</span>
									</Text>
								</Col>
							</PriceView>
							<PriceView
								style={{
									backgroundColor: ColorV2.surface.secondary,
									borderRadius: 16,
								}}
							>
								<Icon
									src={require("../../assets/images/icon/coins.webp")}
								/>
								<Col>
									<Text type="p" weight="medium">
										Te devuelven
									</Text>
									<Text type={"h2"} weight="semibold">
										{priceAmount.split(".")[0]}
										<span
											style={{
												fontSize: 18,
												fontWeight: 500,
											}}
										>
											,{priceAmount.split(".")[1]}€
										</span>
									</Text>
								</Col>
							</PriceView>
						</PriceRow>
						<Text
							type="c2"
							style={{
								color: ColorV2.text.neutralMedium,
								marginTop: 16,
							}}
						>
							*El límite de la deducción sobre la base liquidable se mantiene en el 10%. Para el País Vasco y Navarra consultar regímenes tributarios especiales.
						</Text>
						<Text
							type="c2"
							style={{
								color: ColorV2.text.neutralMedium,
								marginTop: 6,
							}}
						>
							{translation.payment_modal_relief_note_03}
						</Text>
					</Section>
				</ColRight>
			</Container>
		</Modal>
	);
};
export default ReliefModal;
export interface Props {
	isVisible: boolean;
	price: number;
	charge: string;
	onClose: () => void;
}
